import React from 'react';
import { Flex } from 'rebass';
import { Input, Button } from '@lomadee/design-system';
import { Formik } from 'formik';
import MaskedInput from '@plug/components/masked-input';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';
import { OrganizationRegisterPersonalDataSchema } from './OrganizationRegistrationPersonalData.schema';
import { Spacer } from '@plug/ui';
import { TitleForm } from '../../../affiliate-registration/style';
import { useTranslation } from 'react-i18next';

const OrganizationRegistrationPersonalDataStep: React.FC = () => {
  const { values: contextValues, setStep, addValues } = useCreateOrganizationContext();
  const { t } = useTranslation();

  const handleSubmitFormik = (formikValues: CreateOrganizationFormI): void => {
    addValues(formikValues);
    setStep('ORGANIZATION_DATA');
  };

  const handleBackToPasswordStep = (): void => {
    setStep('PASSWORD');
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '32px' }}>
      <Spacer mt="32px" />

      <TitleForm>{t('hubly-dashboard.signup.admin.right-side.second-step.title')}</TitleForm>

      <Formik
        initialValues={contextValues}
        validationSchema={OrganizationRegisterPersonalDataSchema}
        isInitialValid={false}
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          errors,
          isValid,
          touched,
          setFieldValue,
        }): JSX.Element => (
          <>
            <Input
              label={t('hubly-dashboard.signup.admin.right-side.second-step.form.full-name.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.second-step.form.full-name.placeholder',
              )}
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && !!errors.name}
              helperText={touched.name ? errors.name : ''}
              fluid
            />

            <Input
              label={t('hubly-dashboard.signup.admin.right-side.second-step.form.email.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.second-step.form.email.placeholder',
              )}
              name="email"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.email && !!errors.email}
              helperText={touched.email ? errors.email : ''}
              fluid
            />

            <Flex sx={{ gap: '16px' }}>
              <Flex flex={1}>
                <MaskedInput
                  mask="99/99/9999"
                  label={t(
                    'hubly-dashboard.signup.admin.right-side.second-step.form.birthdate.label',
                  )}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.second-step.form.birthdate.placeholder',
                  )}
                  name="birthday"
                  value={values.birthday}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.birthday && !!errors.birthday}
                  helperText={touched.birthday ? errors.birthday : ''}
                  fluid
                />
              </Flex>

              <Flex flex={1}>
                <Input
                  onClickDropdownItem={(clickedValue) =>
                    setFieldValue('gender', clickedValue)
                  }
                  dropdown={[
                    {
                      value: 'male',
                      label: t(
                        'hubly-dashboard.signup.admin.right-side.second-step.form.gender.options.male',
                      ),
                    },
                    {
                      value: 'female',
                      label: t(
                        'hubly-dashboard.signup.admin.right-side.second-step.form.gender.options.female',
                      ),
                    },
                    {
                      value: 'undefined',
                      label: t(
                        'hubly-dashboard.signup.admin.right-side.second-step.form.gender.options.undefined',
                      ),
                    },
                  ]}
                  label={t('hubly-dashboard.signup.admin.right-side.second-step.form.gender.label')}
                  placeholder={t(
                    'hubly-dashboard.signup.admin.right-side.second-step.form.gender.placeholder',
                  )}
                  name="gender"
                  value={
                    values.gender
                      ? (t(
                          `hubly-dashboard.signup.admin.right-side.second-step.form.gender.options.${values.gender}`,
                        ) as string)
                      : ''
                  }
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.gender && !!errors.gender}
                  helperText={touched.gender ? errors.gender : ''}
                  fluid
                />
              </Flex>
            </Flex>

            {/* <Input
              onClickDropdownItem={(clickedValue) => setFieldValue('role', clickedValue)}
              dropdown={[{ value: 'Gerente' }, { value: 'Analista' }]}
              label="Cargo*"
              placeholder="Selecionar"
              name="role"
              value={values.role}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.role && !!errors.role}
              helperText={touched.role ? errors.role : ''}
              fluid
            /> */}

            <MaskedInput
              mask="(99) 99999-9999"
              label={t('hubly-dashboard.signup.admin.right-side.second-step.form.phone.label')}
              placeholder={t(
                'hubly-dashboard.signup.admin.right-side.second-step.form.phone.placeholder',
              )}
              name="phone"
              value={values.phone}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.phone && !!errors.phone}
              helperText={touched.phone ? errors.phone : ''}
              fluid
            />

            <Button type="submit" disabled={!isValid} onClick={(): void => handleSubmit()}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.confirm.text')}
            </Button>

            <Button hierarchy="secondary" onClick={handleBackToPasswordStep}>
              {t('hubly-dashboard.signup.admin.right-side.common.form.buttons.back.text')}
            </Button>
          </>
        )}
      </Formik>
    </Flex>
  );
};

export default OrganizationRegistrationPersonalDataStep;
